import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, Info } from "lucide-react";

export function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let raf; const start = performance.now();
    const from = 0; const to = Number(target) || 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);
  return [ref, val];
}

export function KpiCard({ label, value, suffix = "", trend, hint, accent = "var(--shariti-orange)", onClick, testId, decimals = 0, info }) {
  const [ref, animated] = useCountUp(value);
  const display = decimals ? animated.toFixed(decimals) : Math.round(animated).toLocaleString("fr-FR");
  const TrendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus;
  return (
    <button
      ref={ref}
      onClick={onClick}
      data-testid={testId}
      className={`card-hover group w-full rounded-2xl border border-[var(--border-soft)] bg-white p-5 text-left shadow-[var(--shadow-sm)] ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-ink">
          {label}
          {info && (
            <span title={info} aria-label={info} data-testid={testId ? `${testId}-info` : undefined}
              className="inline-flex cursor-help text-muted-ink/70 transition-colors hover:text-[var(--shariti-orange)]">
              <Info className="h-3.5 w-3.5" />
            </span>
          )}
        </span>
        <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
      </div>
      <div className="mt-3 flex items-end gap-1">
        <span className="text-3xl font-extrabold tracking-tight">{display}</span>
        {suffix && <span className="mb-1 text-sm font-semibold text-muted-ink">{suffix}</span>}
      </div>
      {(hint || trend !== undefined) && (
        <div className="mt-2 flex items-center gap-1.5 text-xs">
          {trend !== undefined && (
            <span className={`inline-flex items-center gap-0.5 font-semibold ${trend > 0 ? "text-[#1F9D55]" : trend < 0 ? "text-[#D64545]" : "text-muted-ink"}`}>
              <TrendIcon className="h-3.5 w-3.5" />
            </span>
          )}
          {hint && <span className="text-muted-ink">{hint}</span>}
        </div>
      )}
    </button>
  );
}

export function Gauge({ value = 0, target = 100, label, sublabel, testId }) {
  const pct = Math.max(0, Math.min(100, value));
  const size = 96; const stroke = 9; const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const ok = value >= target;
  const color = ok ? "#1F9D55" : value >= target * 0.7 ? "#EE722E" : "#D64545";
  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(24,24,24,0.08)" strokeWidth={stroke} />
          <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
            strokeLinecap="round" strokeDasharray={circ}
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: circ - (circ * pct) / 100 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-extrabold">{Math.round(value)}%</span>
        </div>
      </div>
      <div className="mt-1 text-center">
        <div className="text-xs font-bold">{label}</div>
        {sublabel && <div className="text-[10px] text-muted-ink">{sublabel}</div>}
      </div>
    </div>
  );
}

export function SkeletonCard() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white p-5">
      <div className="h-3 w-24 rounded bg-[rgba(24,24,24,0.08)]" />
      <div className="mt-4 h-8 w-20 rounded bg-[rgba(24,24,24,0.08)]" />
      <div className="absolute inset-0 -translate-x-full" style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)", animation: "shimmer 1.4s infinite" }} />
    </div>
  );
}
